import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Box, Button, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import type { SxProps, Theme } from "@mui/material/styles";
import { isMobile, isTablet } from "react-device-detect";
import StepContainer from "./StepContainer";
import Product from "../../../../api/entities/Product";
import { OnboardingContext } from "../../../../store/OnboardingContext";
import { getProducts } from "../../../../api/client";
import {
  MEMBER_STATUS_STANDARD,
  MEMBER_STATUS_TRIAL,
} from "../../../../api/entities/UserProfile";
import BuyNowPurchase from "../../../Payment/Steps";
import {
  APP_STORE_URL,
  PLAY_STORE_URL,
} from "../../../common/InstallApp/links";
import appleappstore from "../../../Modals/app-store-logo.png";
import googleplaystore from "../../../Modals/google-play-logo.png";
// The code to scan holds the get-the-app link (`GET_APP_LINK`, `links.ts`):
// a phone with the app opens it, one without goes to its own store (#1264;
// it held a smart.link before, which sent phones to the sign-up page). Made,
// from app-web/, with:
//   npx --yes qrcode@1.5.4 -t png -w 260 -q 2 -e M -o src/assets/signup-get-app-qr.png "https://link.farmflix.tv/get-app"
// Remake it with the same command if that link changes.
import getAppQr from "../../../../assets/signup-get-app-qr.png";

/**
 * #1245: the free sign-up's last screen, W-SU-END "Get the app" (board
 * FF-1245-End; words from the #1191 plan's words table). Route "try" only
 * (/try, /sign-up, /free-trial and the ad pages); /buy, gift-card and referral
 * sign-ups keep the purchase step.
 *
 * **SUBSCRIPTIONS / MEMBERSHIP ESCALATION CLASS.** This screen starts no
 * trial and takes no payment itself. "View Membership Plans" mounts today's
 * plan chooser exactly as `SignUpStepPurchase.tsx` does; after paying, the
 * member's new status sends them to /videos through `SignUpStepSuccess`.
 *
 * Styles copied from `SignUpStepPurchase.tsx` (`infoBoxSx`, `subHeadingSx`,
 * `bodyTextSx`), with the board's 2 labelled differences: body lines in the
 * theme's text colour on dark (`common.lightgray`, #e9e9e9) rather than the
 * purchase step's #999, and a named `background.ffNavyLighter` divider. Every
 * text colour is named (app-web CLAUDE.md: MUI's defaults assume a light
 * background).
 *
 * #1261: the "No smartphone or tablet?" website-trial card was removed from
 * this screen on Ellaie's call, 4 Nov. My Account keeps its card.
 */

// Copied from SignUpStepPurchase.tsx (was: useStyles().infoBox).
const infoBoxSx: SxProps<Theme> = (theme) => ({
  textAlign: "center",
  marginTop: theme.spacing(3),
});

// Copied from SignUpStepPurchase.tsx (was: useStyles().subHeading), with the
// heading's colour named.
const subHeadingSx: SxProps<Theme> = (theme) => ({
  color: "common.white",
  fontSize: "1.6rem",
  lineHeight: "1.6rem",
  marginBottom: theme.spacing(1),
  [theme.breakpoints.up("sm")]: {
    fontSize: "1.6rem",
    lineHeight: "1.6rem",
  },
  [theme.breakpoints.up("md")]: {
    fontSize: "2rem",
    lineHeight: "2.2rem",
  },
});

// SignUpStepPurchase.tsx's bodyTextSx, in the theme's text colour on dark.
const bodyTextSx: SxProps<Theme> = {
  color: "common.lightgray",
  textAlign: "center",
  fontWeight: 400,
};

const badgesSx: SxProps<Theme> = (theme) => ({
  marginTop: theme.spacing(2),
  display: "flex",
  justifyContent: "center",
  flexWrap: "wrap",
});

// Copied from common/InstallApp/HeroLayout.tsx (the home page's badges).
const AppStoreLogo = styled("img")(({ theme }) => ({
  maxWidth: "140px",
  margin: "5px",
  textAlign: "center",
  [theme.breakpoints.up("sm")]: {
    margin: "10px 5px",
    maxWidth: "130px",
  },
  [theme.breakpoints.up("md")]: {
    margin: "10px 5px",
    maxWidth: "160px",
  },
}));

const QrCode = styled("img")(({ theme }) => ({
  display: "block",
  width: "130px",
  height: "130px",
  margin: `${theme.spacing(2)} auto 0`,
  backgroundColor: theme.palette.common.white,
}));

const dividerSx: SxProps<Theme> = (theme) => ({
  marginTop: theme.spacing(4),
  borderTop: "1px solid",
  borderColor: "background.ffNavyLighter",
});

// Outlined white, so the app part stays first (board FF-1191-Buy-B). Width,
// margin and size from SignUpStepPurchase.tsx's primaryButtonSx; colours as
// YouTubeLanding.tsx's outlinedButtonSx, repeated under &:hover because the
// theme's outlined hover rule sets them back to near-black.
const plansButtonSx: SxProps<Theme> = {
  width: "80%",
  margin: "12px 5%",
  fontSize: "1.1rem",
  color: "common.white",
  borderColor: "common.white",
  "&:hover": {
    color: "common.white",
    borderColor: "common.white",
  },
};

// Exact words from the plan's words table. The trial line's "72-hour" is
// written with a non-breaking hyphen (\u2011) so it doesn't break across lines at 390 wide.
const HEADING = "Your account is ready";
const LINE_PHONE = "Get the app to start watching.";
const LINE_COMPUTER =
  "Get the FarmFLiX app on your phone or tablet to start watching.";
const CODE_LINE = "Or point your phone's camera at this code to get the app.";
const LOGIN_NEW =
  "Then log in to the app with the email and password you just chose.";
const LOGIN_RETURNING =
  'Then log in to the app with the email and password you already use for FarmFLiX, or tap "Login using your Phone Number" and use this mobile.';
const TRIAL_LINE = "Your free 72\u2011hour trial is waiting in the app.";
const PLANS_QUESTION = "Want to watch every episode now?";

const SignUpStepGetApp = () => {
  const [buyOpen, setBuyOpen] = useState(false);
  const [availableProducts, setAvailableProducts] = useState<Product[]>();
  const { runWithLoader, application } = useContext(OnboardingContext);

  // Phones and tablets get the phone version; everything else (computers AND
  // smart-TV browsers, where react-device-detect reports isMobile, isTablet
  // and isBrowser all false) gets the computer version with the code to scan.
  // An if/else with no third case, as HeroLayout.tsx.
  const phoneOrTablet = isMobile || isTablet;

  // A mobile already on an account skips the details step, so this person
  // chose no password just now (#1191 plan, S18). Their email is never shown.
  const returning = !!application?.phoneExists;

  // Copied from SignUpStepPurchase.tsx. Products come from a signed-in call,
  // so a trial product is listed only when this account can start a trial.
  const trialProduct = useMemo(
    () =>
      availableProducts?.find((p) => p.memberStatusId === MEMBER_STATUS_TRIAL),
    [availableProducts],
  );
  const standardProducts = useMemo(
    () =>
      availableProducts?.filter(
        (p) => p.memberStatusId === MEMBER_STATUS_STANDARD,
      ),
    [availableProducts],
  );

  const viewPlans = useCallback(() => {
    if (!standardProducts) return;
    setBuyOpen(true);
  }, [standardProducts]);

  useEffect(() => {
    runWithLoader(async () => {
      const products = await getProducts();
      setAvailableProducts(products.results);
    }, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hasTrial = !!trialProduct;
  const hasBuy = !!standardProducts && standardProducts.length > 0;

  return (
    <StepContainer>
      <StepContainer.Body>
        {buyOpen ? (
          <BuyNowPurchase
            dark={true}
            title="Choose your Plan"
            showStepper={false}
          />
        ) : (
          <Box sx={infoBoxSx}>
            <Typography variant="subtitle1" sx={subHeadingSx}>
              {HEADING}
            </Typography>
            <Typography variant="body1" sx={bodyTextSx}>
              {phoneOrTablet ? LINE_PHONE : LINE_COMPUTER}
            </Typography>

            <Box sx={badgesSx}>
              <a href={APP_STORE_URL} target="_blank" rel="noopener noreferrer">
                <AppStoreLogo
                  src={appleappstore}
                  alt="Apple App Store Graphic"
                />
              </a>
              <a
                href={PLAY_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                <AppStoreLogo
                  src={googleplaystore}
                  alt="Google Play Store Graphic"
                />
              </a>
            </Box>

            {!phoneOrTablet && (
              <>
                <QrCode
                  src={getAppQr}
                  alt="Code to scan for the FarmFLiX app"
                />
                <Typography variant="body1" sx={[bodyTextSx, { mt: 1 }]}>
                  {CODE_LINE}
                </Typography>
              </>
            )}

            <Typography variant="body1" sx={[bodyTextSx, { mt: 3 }]}>
              {returning ? LOGIN_RETURNING : LOGIN_NEW}
            </Typography>

            {hasTrial && (
              <Typography variant="body1" sx={[bodyTextSx, { mt: 1.5 }]}>
                {TRIAL_LINE}
              </Typography>
            )}

            {hasBuy && (
              <>
                <Box sx={dividerSx} />
                <Typography variant="body1" sx={[bodyTextSx, { mt: 3 }]}>
                  {PLANS_QUESTION}
                </Typography>
                <Button
                  name="buy"
                  variant="outlined"
                  size="medium"
                  onClick={viewPlans}
                  sx={plansButtonSx}
                >
                  View Membership Plans
                </Button>
              </>
            )}
          </Box>
        )}
      </StepContainer.Body>
      <StepContainer.Actions />
    </StepContainer>
  );
};

export default SignUpStepGetApp;
